import { useDispatch, useSelector } from "react-redux";
import { FC, useEffect, createContext, useContext } from "react";
import * as _ from "lodash";

import { isDispatched, isFulfilled, isPending } from "@ctra/utils";
import { Enterprise, EnterpriseAppState, AskIda, TrackerList, TrackerEntity } from "@ctra/api";

interface ContextType {
  trackerList: TrackerList;
  meta: { isLoading: boolean; isArchived?: boolean };
}

interface ProviderProps {
  id?: TrackerEntity["id"];
  query?: Record<string, unknown>;
}

/**
 * Make a default context for QnA list
 */
const DefaultContext = createContext<ContextType>({
  trackerList: {},
  meta: { isLoading: true, isArchived: false }
});

/**
 * Fetch the questions and answers object if it is not yet present in the store
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @param {number | undefined} id
 * @param {Record<string, unknown> | undefined} query
 * @return {JSX.Element}
 */
const _QnAProvider: FC<ProviderProps> = ({ children, id, query }) => {
  const dispatch = useDispatch();
  const dataQuery = id ? { filter: { IdaQnAID: id } } : query;

  /**
   * Get the questions and answers from the state
   * @type {TrackerList}
   */
  const trackerList = useSelector<EnterpriseAppState, TrackerList>((state) =>
    Enterprise.entities.getQnAs(state, dataQuery)
  );

  /**
   * Tell whether the fetching action has been dispatched
   * @type {boolean}
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      !!dataQuery &&
      isDispatched(state, AskIda.types.FETCH_ASK_IDA_TRACKER_LIST, {
        primaryValue: AskIda.utils.makeQueryHash(dataQuery)
      })
  );

  /**
   * Tell whether we are loading
   * @type {boolean}
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      !dataQuery ||
      isPending(state, AskIda.types.FETCH_ASK_IDA_TRACKER_LIST, {
        primaryValue: AskIda.utils.makeQueryHash(dataQuery)
      })
  );

  /**
   * Tell whether the api has done fetching
   * @type {boolean}
   */
  const fullfilled = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      !dataQuery ||
      isFulfilled(state, AskIda.types.FETCH_ASK_IDA_TRACKER_LIST, {
        primaryValue: AskIda.utils.makeQueryHash(dataQuery)
      })
  );

  useEffect(() => {
    if (!dispatched && dataQuery) {
      dispatch(AskIda.actions.fetchTrackerList.start(dataQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dispatched, trackerList, query, id]);

  /**
   * @todo create proper archived state
   */
  const isArchived = !!id && fullfilled && _.isEmpty(trackerList);

  return (
    <DefaultContext.Provider
      value={{
        trackerList,
        meta: { isLoading, isArchived }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const QnAContext = {
  Consumer: DefaultContext.Consumer,
  Provider: _QnAProvider
};

/**
 * Hook to get the qna list within the context
 */
export const useQnAs = (): ContextType => useContext(DefaultContext);
